import React, { useEffect, useMemo, useState } from "react";
import { usePagos } from "../state/PagosContext";
import { api } from "../api";

function catLabel(c) {
  const parts = [c.level1, c.level2, c.level3, c.name].filter(Boolean);
  return parts.join(" / ");
}

function bankLabel(name) {
  return name || "—";
}

export default function Configuracion() {
  const { session, valuadores, bankAccounts, catalogo, refreshAll } = usePagos();

  const role = session?.user?.role || "";
  const isAdmin = role === "admin";
  const canEdit = role === "admin" || role === "captura";

  const [tab, setTab] = useState("catalogo"); // catalogo | cuentas | valuadores | usuarios

  // ===== Usuarios (admin)
  const [users, setUsers] = useState([]);
  const [uForm, setUForm] = useState({ username: "", password: "", role: "uso" });

  // ===== Cuentas banco (company/other)
  const [baForm, setBaForm] = useState({ scope: "worth", direction: "out", name: "", owner_type: "company" });

  // ===== Valuadores
  const [vNew, setVNew] = useState({
    name: "",
    out_ids: [],
    in_accounts: [{ scope: "worth", bank: "", last4: "" }],
  });
  const [vAccounts, setVAccounts] = useState({}); // { [valuadorId]: {out:[], in:[]} }
  const [msg, setMsg] = useState({ type: "", text: "" });

  // ===== Catálogo
  const [catType, setCatType] = useState("all"); // all | expense | income
  const [catSearch, setCatSearch] = useState("");

  useEffect(() => {
    (async () => {
      if (!isAdmin) return;
      try {
        const r = await api.listUsers();
        setUsers(r);
      } catch {}
    })();
  }, [isAdmin]);

  async function reloadUsers() {
    if (!isAdmin) return;
    const r = await api.listUsers();
    setUsers(r);
  }

  async function createUser(e) {
    e.preventDefault();
    if (!isAdmin) return;
    setMsg({ type: "", text: "" });
    try {
      await api.createUser({ ...uForm, username: uForm.username.trim() });
      setUForm({ username: "", password: "", role: "uso" });
      await reloadUsers();
      setMsg({ type: "ok", text: "Usuario creado." });
    } catch (e2) {
      setMsg({ type: "error", text: e2.message === "username_exists" ? "Ese usuario ya existe." : "No se pudo crear el usuario." });
    }
  }

  async function resetPassword(userId) {
    if (!isAdmin) return;
    const p = prompt("Nueva contraseña (mín. 8 caracteres):");
    if (!p) return;
    try {
      await api.resetPassword(userId, p);
      setMsg({ type: "ok", text: "Contraseña actualizada." });
    } catch {
      setMsg({ type: "error", text: "No se pudo actualizar la contraseña." });
    }
  }

  // ===== Cuentas banco
  const bankAccountsCompany = useMemo(() => {
    return bankAccounts.filter((b) => b.owner_type !== "valuator");
  }, [bankAccounts]);

  const bankAccountsOut = useMemo(() => {
    return bankAccountsCompany.filter((b) => b.direction === "out");
  }, [bankAccountsCompany]);

  async function addBankAccount(e) {
    e.preventDefault();
    if (!canEdit) return;
    if (!baForm.name.trim()) return;
    setMsg({ type: "", text: "" });
    try {
      await api.createBankAccount({
        scope: baForm.scope,
        direction: baForm.direction,
        name: baForm.name.trim(),
        owner_type: baForm.owner_type,
        owner_id: null,
      });
      setBaForm((f) => ({ ...f, name: "" }));
      await refreshAll();
      setMsg({ type: "ok", text: "Cuenta agregada." });
    } catch {
      setMsg({ type: "error", text: "No se pudo agregar la cuenta." });
    }
  }

  // ===== Valuadores + regla cuentas
  async function loadValuadorCuentas(id) {
    if (!session?.token) return;
    if (vAccounts[id]) return;
    try {
      const r = await api.getValuadorCuentas(id);
      setVAccounts((prev) => ({ ...prev, [id]: r }));
    } catch {}
  }

  async function addValuador(e) {
    e.preventDefault();
    if (!canEdit) return;
    setMsg({ type: "", text: "" });

    const name = vNew.name.trim();
    if (name.length < 2) return setMsg({ type: "error", text: "Nombre de valuador inválido." });

    const outIds = (vNew.out_ids || []).map(Number).filter(Boolean);
    const inAcc = (vNew.in_accounts || []).filter((x) => (x.bank || "").trim() && (x.last4 || "").trim().length === 4);

    if (outIds.length < 1) return setMsg({ type: "error", text: "Selecciona al menos 1 cuenta de salida (empresa)." });
    if (inAcc.length < 1) return setMsg({ type: "error", text: "Agrega al menos 1 cuenta de entrada (banco + últimos 4)." });

    try {
      const v = await api.createValuador(name);

      // crear cuentas de entrada del valuador (direction=in, owner_type=valuator)
      const createdInIds = [];
      for (const a of inAcc) {
        const label = `${a.bank.trim()} ****${a.last4.trim()}`;
        const r = await api.createBankAccount({
          scope: a.scope || "worth",
          direction: "in",
          name: label,
          owner_type: "valuator",
          owner_id: v.id,
        });
        createdInIds.push(r.id);
      }

      // asignar regla (1+ out, 1+ in)
      await api.setValuadorCuentas(v.id, { out: outIds, in: createdInIds });

      setVNew({ name: "", out_ids: [], in_accounts: [{ scope: "worth", bank: "", last4: "" }] });
      await refreshAll();
      setVAccounts((prev) => ({ ...prev, [v.id]: { out: outIds, in: createdInIds } }));
      setMsg({ type: "ok", text: "Valuador creado y cuentas asignadas." });
    } catch (e2) {
      setMsg({ type: "error", text: e2.message === "needs_in_and_out_accounts" ? "Falta salida/entrada." : "No se pudo crear el valuador." });
    }
  }

  async function saveValuadorAsignacion(id, nextOut, nextIn) {
    if (!canEdit) return;
    setMsg({ type: "", text: "" });
    try {
      await api.setValuadorCuentas(id, { out: nextOut, in: nextIn });
      setVAccounts((prev) => ({ ...prev, [id]: { out: nextOut, in: nextIn } }));
      setMsg({ type: "ok", text: "Asignación guardada." });
    } catch (e2) {
      setMsg({ type: "error", text: e2.message === "needs_in_and_out_accounts" ? "Debe tener al menos 1 salida y 1 entrada." : "No se pudo guardar." });
    }
  }

  async function addValuadorInAccount(valuadorId, scope, bank, last4) {
    if (!canEdit) return;
    const b = (bank || "").trim();
    const l4 = (last4 || "").trim();
    if (!b || l4.length !== 4) return;
    setMsg({ type: "", text: "" });
    try {
      const r = await api.createBankAccount({
        scope: scope || "worth",
        direction: "in",
        name: `${b} ****${l4}`,
        owner_type: "valuator",
        owner_id: valuadorId,
      });
      await refreshAll();
      // auto-agregar a asignación
      const cur = vAccounts[valuadorId] || (await api.getValuadorCuentas(valuadorId));
      const nextIn = Array.from(new Set([...(cur.in || []), r.id]));
      const nextOut = cur.out || [];
      await saveValuadorAsignacion(valuadorId, nextOut, nextIn);
    } catch {
      setMsg({ type: "error", text: "No se pudo agregar la cuenta del valuador." });
    }
  }

  const catalogoVisible = useMemo(() => {
    const s = catSearch.trim().toLowerCase();
    return catalogo
      .filter((c) => (catType === "all" ? true : c.type === catType))
      .filter((c) => {
        if (!s) return true;
        const lbl = catLabel(c).toLowerCase();
        return lbl.includes(s) || (c.scope || "").toLowerCase().includes(s) || (c.type || "").toLowerCase().includes(s);
      });
  }, [catalogo, catType, catSearch]);

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold text-worthblue">Configuración</h1>
        <div className="text-sm text-gray-600">
          Perfil: <b>{role || "—"}</b>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 p-3 rounded ${msg.type === "ok" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
          {msg.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button className={`px-3 py-1 rounded ${tab === "catalogo" ? "bg-worthblue text-white" : "bg-gray-100 hover:bg-gray-200"}`} onClick={() => setTab("catalogo")}>
          Catálogo
        </button>
        <button className={`px-3 py-1 rounded ${tab === "cuentas" ? "bg-worthblue text-white" : "bg-gray-100 hover:bg-gray-200"}`} onClick={() => setTab("cuentas")}>
          Cuentas bancarias
        </button>
        <button className={`px-3 py-1 rounded ${tab === "valuadores" ? "bg-worthblue text-white" : "bg-gray-100 hover:bg-gray-200"}`} onClick={() => setTab("valuadores")}>
          Valuadores
        </button>
        {isAdmin && (
          <button className={`px-3 py-1 rounded ${tab === "usuarios" ? "bg-worthblue text-white" : "bg-gray-100 hover:bg-gray-200"}`} onClick={() => setTab("usuarios")}>
            Usuarios
          </button>
        )}
      </div>

      {/* ===== Catálogo ===== */}
      {tab === "catalogo" && (
        <div className="bg-white rounded-2xl shadow p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex gap-2 items-center">
              <select className="border rounded px-2 py-1" value={catType} onChange={(e) => setCatType(e.target.value)}>
                <option value="all">Todos</option>
                <option value="expense">Gastos</option>
                <option value="income">Ingresos</option>
              </select>
              <input className="border rounded px-3 py-1" placeholder="Buscar..." value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
            </div>
            <div className="text-xs text-gray-500">En captura solo se usan Gastos (expense). Ingresos están en BD.</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b text-worthblue font-bold">
                  <th className="py-2">Scope</th>
                  <th>Tipo</th>
                  <th>Cuenta</th>
                </tr>
              </thead>
              <tbody>
                {catalogoVisible.map((c) => (
                  <tr key={c.id} className="border-b">
                    <td className="py-1">{c.scope}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${c.type === "income" ? "bg-green-50 text-green-800" : "bg-gray-100 text-gray-700"}`}>
                        {c.type}
                      </span>
                    </td>
                    <td>{catLabel(c)}</td>
                  </tr>
                ))}
                {catalogoVisible.length === 0 && (
                  <tr><td colSpan={3} className="text-center text-gray-400 py-6">Sin resultados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Cuentas bancarias ===== */}
      {tab === "cuentas" && (
        <div className="bg-white rounded-2xl shadow p-4">
          {!canEdit && (
            <div className="mb-3 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
              Tu perfil no permite crear/editar cuentas. Puedes ver el listado.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h2 className="font-bold text-worthblue mb-2">Agregar cuenta (empresa/otros)</h2>
              <form onSubmit={addBankAccount} className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <select className="border rounded px-2 py-1" value={baForm.scope} onChange={(e) => setBaForm((f) => ({ ...f, scope: e.target.value }))} disabled={!canEdit}>
                    <option value="worth">worth</option>
                    <option value="jpgm">jpgm</option>
                  </select>
                  <select className="border rounded px-2 py-1" value={baForm.direction} onChange={(e) => setBaForm((f) => ({ ...f, direction: e.target.value }))} disabled={!canEdit}>
                    <option value="out">salida (pago)</option>
                    <option value="in">entrada (destino)</option>
                  </select>
                  <select className="border rounded px-2 py-1" value={baForm.owner_type} onChange={(e) => setBaForm((f) => ({ ...f, owner_type: e.target.value }))} disabled={!canEdit}>
                    <option value="company">empresa</option>
                    <option value="other">otro</option>
                  </select>
                </div>
                <input className="border rounded px-3 py-1" placeholder="Ej. BBVA Empresa / Banorte ****1234 / CFE" value={baForm.name} onChange={(e) => setBaForm((f) => ({ ...f, name: e.target.value }))} disabled={!canEdit} />
                <button className="px-3 py-2 rounded bg-worthblue text-white font-semibold hover:bg-worthgold disabled:opacity-50" disabled={!canEdit}>
                  Guardar
                </button>
              </form>
            </div>

            <div>
              <h2 className="font-bold text-worthblue mb-2">Listado</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className="border-b text-worthblue font-bold">
                      <th className="py-2">Scope</th>
                      <th>Dir</th>
                      <th>Nombre</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankAccounts.map((b) => (
                      <tr key={b.id} className="border-b">
                        <td className="py-1">{b.scope}</td>
                        <td>{b.direction}</td>
                        <td>{bankLabel(b.name)}</td>
                        <td>{b.owner_type}{b.owner_id ? `:${b.owner_id}` : ""}</td>
                      </tr>
                    ))}
                    {bankAccounts.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-gray-400 py-6">Sin cuentas</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Valuadores ===== */}
      {tab === "valuadores" && (
        <div className="bg-white rounded-2xl shadow p-4">
          {!canEdit && (
            <div className="mb-3 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
              Tu perfil no permite crear/editar valuadores. Puedes ver el listado.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="font-bold text-worthblue mb-2">Alta de valuador (con regla de cuentas)</h2>
              <form onSubmit={addValuador} className="flex flex-col gap-2">
                <input className="border rounded px-3 py-1" placeholder="Nombre del valuador" value={vNew.name} onChange={(e) => setVNew((x) => ({ ...x, name: e.target.value }))} disabled={!canEdit} />

                <div className="text-sm font-semibold text-gray-700 mt-2">Cuentas de salida (empresa)</div>
                <select
                  className="border rounded px-2 py-1"
                  multiple
                  value={(vNew.out_ids || []).map(String)}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                    setVNew((x) => ({ ...x, out_ids: opts }));
                  }}
                  disabled={!canEdit}
                >
                  {bankAccountsOut.map((b) => (
                    <option key={b.id} value={b.id}>
                      [{b.scope}] {b.name}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-500">Ctrl/Command para seleccionar varias.</div>

                <div className="text-sm font-semibold text-gray-700 mt-2">Cuentas de entrada (del valuador)</div>
                {(vNew.in_accounts || []).map((a, idx) => (
                  <div key={idx} className="grid grid-cols-6 gap-2 items-center">
                    <select className="border rounded px-2 py-1 col-span-2" value={a.scope} onChange={(e) => {
                      const v = e.target.value;
                      setVNew((x) => {
                        const next = [...x.in_accounts];
                        next[idx] = { ...next[idx], scope: v };
                        return { ...x, in_accounts: next };
                      });
                    }} disabled={!canEdit}>
                      <option value="worth">worth</option>
                      <option value="jpgm">jpgm</option>
                    </select>
                    <input className="border rounded px-2 py-1 col-span-3" placeholder="Banco" value={a.bank} onChange={(e) => {
                      const v = e.target.value;
                      setVNew((x) => {
                        const next = [...x.in_accounts];
                        next[idx] = { ...next[idx], bank: v };
                        return { ...x, in_accounts: next };
                      });
                    }} disabled={!canEdit} />
                    <input className="border rounded px-2 py-1 col-span-1" placeholder="1234" maxLength={4} value={a.last4} onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0,4);
                      setVNew((x) => {
                        const next = [...x.in_accounts];
                        next[idx] = { ...next[idx], last4: v };
                        return { ...x, in_accounts: next };
                      });
                    }} disabled={!canEdit} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => setVNew((x) => ({ ...x, in_accounts: [...x.in_accounts, { scope: "worth", bank: "", last4: "" }] }))} disabled={!canEdit}>
                    + Agregar cuenta entrada
                  </button>
                </div>

                <button className="mt-2 px-3 py-2 rounded bg-worthblue text-white font-semibold hover:bg-worthgold disabled:opacity-50" disabled={!canEdit}>
                  Crear valuador
                </button>
              </form>
            </div>

            <div>
              <h2 className="font-bold text-worthblue mb-2">Valuadores existentes</h2>
              <div className="space-y-3">
                {valuadores.map((v) => (
                  <ValuadorCard
                    key={v.id}
                    valuador={v}
                    canEdit={canEdit}
                    bankAccounts={bankAccounts}
                    bankAccountsOut={bankAccountsOut}
                    loadValuadorCuentas={loadValuadorCuentas}
                    vAccounts={vAccounts}
                    saveValuadorAsignacion={saveValuadorAsignacion}
                    addValuadorInAccount={addValuadorInAccount}
                  />
                ))}
                {valuadores.length === 0 && (
                  <div className="text-gray-400 text-sm">Sin valuadores</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Usuarios ===== */}
      {tab === "usuarios" && isAdmin && (
        <div className="bg-white rounded-2xl shadow p-4">
          <h2 className="font-bold text-worthblue mb-2">Usuarios</h2>

          <form onSubmit={createUser} className="grid md:grid-cols-4 gap-2 mb-4">
            <input className="border rounded px-3 py-1" placeholder="Usuario" value={uForm.username} onChange={(e) => setUForm((f) => ({ ...f, username: e.target.value }))} />
            <input className="border rounded px-3 py-1" type="password" placeholder="Contraseña (mín 8)" value={uForm.password} onChange={(e) => setUForm((f) => ({ ...f, password: e.target.value }))} />
            <select className="border rounded px-2 py-1" value={uForm.role} onChange={(e) => setUForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="uso">uso</option>
              <option value="captura">captura</option>
              <option value="admin">admin</option>
            </select>
            <button className="px-3 py-2 rounded bg-worthblue text-white font-semibold hover:bg-worthgold">Crear</button>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b text-worthblue font-bold">
                  <th className="py-2">Usuario</th>
                  <th>Rol</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b">
                    <td className="py-1">{u.username}</td>
                    <td>{u.role}</td>
                    <td>{u.is_active ? "sí" : "no"}</td>
                    <td>
                      <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => resetPassword(u.id)}>
                        Reset password
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-gray-400 py-6">Sin usuarios</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ValuadorCard({
  valuador,
  canEdit,
  bankAccounts,
  bankAccountsOut,
  loadValuadorCuentas,
  vAccounts,
  saveValuadorAsignacion,
  addValuadorInAccount,
}) {
  const [open, setOpen] = useState(false);
  const [draftOut, setDraftOut] = useState([]);
  const [draftIn, setDraftIn] = useState([]);
  const [newIn, setNewIn] = useState({ scope: "worth", bank: "", last4: "" });

  const inOptions = useMemo(() => {
    return bankAccounts
      .filter((b) => b.direction === "in" && b.owner_type === "valuator" && Number(b.owner_id) === Number(valuador.id))
      .sort((a,b)=>a.name.localeCompare(b.name));
  }, [bankAccounts, valuador.id]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      await loadValuadorCuentas(valuador.id);
      const cur = vAccounts[valuador.id];
      if (cur) {
        setDraftOut((cur.out || []).map(String));
        setDraftIn((cur.in || []).map(String));
      }
    }
  }

  async function save() {
    const outIds = draftOut.map(Number).filter(Boolean);
    const inIds = draftIn.map(Number).filter(Boolean);
    await saveValuadorAsignacion(valuador.id, outIds, inIds);
  }

  const cur = vAccounts[valuador.id];

  return (
    <div className="border rounded-xl p-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-worthblue">{valuador.name}</div>
        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm" onClick={toggle}>
          {open ? "Cerrar" : "Administrar"}
        </button>
      </div>

      {!open && cur && (
        <div className="text-xs text-gray-600 mt-2">
          <div>Salidas: {(cur.out || []).length}</div>
          <div>Entradas: {(cur.in || []).length}</div>
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-sm font-semibold text-gray-700">Cuentas de salida (empresa)</div>
            <select
              className="border rounded px-2 py-1 w-full"
              multiple
              value={draftOut}
              onChange={(e) => setDraftOut(Array.from(e.target.selectedOptions).map((o) => o.value))}
              disabled={!canEdit}
            >
              {bankAccountsOut.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  [{b.scope}] {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-700">Cuentas de entrada (del valuador)</div>
            <select
              className="border rounded px-2 py-1 w-full"
              multiple
              value={draftIn}
              onChange={(e) => setDraftIn(Array.from(e.target.selectedOptions).map((o) => o.value))}
              disabled={!canEdit}
            >
              {inOptions.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  [{b.scope}] {b.name}
                </option>
              ))}
            </select>

            {canEdit && (
              <div className="grid grid-cols-6 gap-2 items-center mt-2">
                <select className="border rounded px-2 py-1 col-span-2" value={newIn.scope} onChange={(e) => setNewIn((x) => ({ ...x, scope: e.target.value }))}>
                  <option value="worth">worth</option>
                  <option value="jpgm">jpgm</option>
                </select>
                <input className="border rounded px-2 py-1 col-span-3" placeholder="Banco" value={newIn.bank} onChange={(e) => setNewIn((x) => ({ ...x, bank: e.target.value }))} />
                <input className="border rounded px-2 py-1 col-span-1" placeholder="1234" maxLength={4} value={newIn.last4} onChange={(e) => setNewIn((x) => ({ ...x, last4: e.target.value.replace(/\D/g, "").slice(0,4) }))} />
                <div className="col-span-6 flex justify-end">
                  <button
                    type="button"
                    className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                    onClick={async () => {
                      await addValuadorInAccount(valuador.id, newIn.scope, newIn.bank, newIn.last4);
                      setNewIn({ scope: "worth", bank: "", last4: "" });
                    }}
                  >
                    + Agregar cuenta entrada
                  </button>
                </div>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1 rounded bg-worthblue text-white font-semibold hover:bg-worthgold" onClick={save}>
                Guardar asignación
              </button>
            </div>
          )}
          {!canEdit && (
            <div className="text-xs text-gray-500">Solo lectura (perfil sin edición).</div>
          )}
        </div>
      )}
    </div>
  );
}
